// Recipe nutrition — port of Swift NutritionSummary + NutritionDailyTargets.
// Stored values: calories in kcal, everything else in integer milligrams (as in Swift).
// Δ: recipes now store amounts for their BASE servings (decision 1), so results are divided
// by servings to show "per serving" — Swift's numbers were already per serving.
import { toNumber } from './quantity';
import { resolveUnit, type CustomUnit } from './units';
import { AGE_MIDDLE, dietPlan, type DietProfile } from './dietPlan';
import type { Id, Ingredient, IngredientLine, Nutrition } from '../types';

type Category = 'Minerals' | 'Vitamins' | 'Common nutrients' | 'Advanced nutrients';
type Micro = { key: string; title: string; category: Category; advanced: boolean };

const m = (category: Category, advanced: boolean, pairs: [string, string][]): Micro[] =>
  pairs.map(([key, title]) => ({ key, title, category, advanced }));

/** Same lists, titles and order as the Swift breakdown. */
export const MICRONUTRIENTS: Micro[] = [
  ...m('Minerals', false, [['iron', 'Iron'], ['zinc', 'Zinc'], ['manganese', 'Manganese'], ['copper', 'Copper'], ['iodine', 'Iodine'], ['selenium', 'Selenium']]),
  ...m('Minerals', true, [['cobalt', 'Cobalt'], ['molybdenum', 'Molybdenum'], ['bromine', 'Bromine']]),
  ...m('Minerals', false, [['calcium', 'Calcium'], ['chlorine', 'Chlorine'], ['magnesium', 'Magnesium'], ['phosphorus', 'Phosphorus'], ['potassium', 'Potassium'], ['sodium', 'Sodium'], ['sulfur', 'Sulfur']]),
  ...m('Vitamins', false, [
    ['vitaminA', 'Vitamin A'], ['vitaminB', 'Vitamin B'], ['vitaminB1', 'Vitamin B1'], ['vitaminB2', 'Vitamin B2'],
    ['vitaminB3', 'Vitamin B3'], ['vitaminB5', 'Vitamin B5'], ['vitaminB6', 'Vitamin B6'], ['vitaminB7', 'Vitamin B7'],
    ['vitaminB9', 'Vitamin B9'], ['vitaminB12', 'Vitamin B12'], ['vitaminC', 'Vitamin C'], ['vitaminD', 'Vitamin D'],
    ['vitaminE', 'Vitamin E'], ['vitaminK', 'Vitamin K'], ['vitaminK1', 'Vitamin K1'], ['vitaminK2', 'Vitamin K2'],
  ]),
  ...m('Common nutrients', false, [
    ['fiber', 'Fiber'], ['addedSugar', 'Added sugar'], ['starch', 'Starch'], ['saturatedFat', 'Saturated fat'],
    ['monounsaturatedFat', 'Monounsaturated fat'], ['polyunsaturatedFat', 'Polyunsaturated fat'], ['omega3', 'Omega-3'], ['omega6', 'Omega-6'],
  ]),
  ...m('Advanced nutrients', true, [
    ['monosaccharide', 'Monosaccharides'], ['glucose', 'Glucose'], ['fructose', 'Fructose'], ['disaccharide', 'Disaccharides'],
    ['sucrose', 'Sucrose'], ['lactose', 'Lactose'], ['maltose', 'Maltose'], ['polysaccharide', 'Polysaccharides'],
    ['oligosaccharide', 'Oligosaccharides'], ['galactans', 'Galactans'], ['fructans', 'Fructans'], ['raffinose', 'Raffinose'],
    ['stachyose', 'Stachyose'], ['transFat', 'Trans fat'], ['omega7', 'Omega-7'], ['omega9', 'Omega-9'],
    ['leucine', 'Leucine'], ['isoleucine', 'Isoleucine'], ['valine', 'Valine'], ['lysine', 'Lysine'],
    ['phenylalanine', 'Phenylalanine'], ['threonine', 'Threonine'], ['tryptophan', 'Tryptophan'], ['methionine', 'Methionine'],
    ['histidine', 'Histidine'],
  ]),
];

const MACROS = ['protein', 'fat', 'carbohydrates', 'sugar', 'fiber'] as const;

/**
 * Swift per100GramMultiplier. Quirk kept on purpose: "per 100 g" only applies when at least one
 * of calories/protein/fat/carbs/sugar/fiber is present; otherwise values count per item ×
 * max(amount, 1). Per-100 g data needs a MASS unit, else the ingredient counts as missing.
 */
export function multiplier(line: IngredientLine, nutrition: Nutrition, customUnits: Record<string, CustomUnit>): number | null {
  const v = nutrition.values;
  const canonical = nutrition.basis === 'per100Grams' && ['calories', ...MACROS].some(k => v[k] != null);
  const amount = toNumber(line.amount);
  if (!canonical) return Math.max(amount, 1);
  const unit = resolveUnit(line.unit, customUnits);
  if (!unit || unit.dimension !== 'mass' || !unit.toBase) return null;
  return amount * unit.toBase * 10; // kg × 10 = number of 100 g portions
}

export type NutritionSummary = {
  calories: number;
  /** grams */
  protein: number;
  fat: number;
  carbohydrates: number;
  sugar: number;
  fiber: number;
  /** mg, summed per micronutrient key */
  micros: Record<string, number>;
  knownIngredients: number;
  missingTitles: string[];
};

export function summarize(
  lines: IngredientLine[],
  ingredients: Record<Id, Ingredient>,
  customUnits: Record<string, CustomUnit>,
  divideBy = 1,
): NutritionSummary {
  const s: NutritionSummary = { calories: 0, protein: 0, fat: 0, carbohydrates: 0, sugar: 0, fiber: 0, micros: {}, knownIngredients: 0, missingTitles: [] };
  const d = Math.max(divideBy, 1);
  for (const line of lines) {
    const ing = ingredients[line.ingredientId];
    if (!ing) continue;
    const mult = ing.nutrition ? multiplier(line, ing.nutrition, customUnits) : null;
    if (!ing.nutrition || mult === null) {
      s.missingTitles.push(ing.title);
      continue;
    }
    const v = ing.nutrition.values;
    let known = false;
    if (v.calories != null) {
      s.calories += (v.calories * mult) / d;
      known = true;
    }
    for (const k of MACROS) {
      if (v[k] != null) {
        s[k] += ((v[k]! / 1000) * mult) / d;
        known = true;
      }
    }
    for (const micro of MICRONUTRIENTS) {
      const value = v[micro.key];
      if (value != null && value > 0) {
        s.micros[micro.key] = (s.micros[micro.key] ?? 0) + (value * mult) / d;
        known = true;
      }
    }
    if (known) s.knownIngredients += 1;
    else s.missingTitles.push(ing.title);
  }
  return s;
}

/** Donut slices in grams: Sugar, Carbs (carbs − sugar), Protein, Fat; zero slices dropped. */
export function macroSlices(s: NutritionSummary): { title: string; grams: number }[] {
  return [
    { title: 'Sugar', grams: s.sugar },
    { title: 'Carbs', grams: Math.max(s.carbohydrates - s.sugar, 0) },
    { title: 'Protein', grams: s.protein },
    { title: 'Fat', grams: s.fat },
  ].filter(x => x.grams > 0);
}

const REFERENCE = { calories: 2000, protein: 50, fat: 65, carbohydrates: 275, sugar: 36, fiber: 25, vitamins: 100, minerals: 1000, aminoAcids: 50000, fattyAcids: 65000 };

/**
 * Daily targets for the breakdowns: the general reference without a diet profile, else the diet
 * plan's (logic/dietPlan.ts). Group targets for vitamins, minerals, amino and fatty acids keep the
 * Swift app's scaling. `micros` holds the plan's micronutrients in mg (sodium is a maximum).
 */
export function dailyTargets(p: DietProfile | null): typeof REFERENCE & { micros: Partial<Record<string, { value: number; text: string; max?: boolean }>> } {
  if (!p) return { ...REFERENCE, micros: {} };
  const plan = dietPlan(p);
  const weightFactor = Math.max(p.weightKg, 1) / 70;
  const years = AGE_MIDDLE[p.age];
  const ageFactor = years < 18 ? 1.1 : years > 65 ? 0.9 : 1;
  return {
    calories: plan.calories,
    protein: plan.protein,
    fat: plan.fat,
    carbohydrates: plan.carbohydrates,
    sugar: plan.sugar,
    fiber: plan.fiber,
    vitamins: 100 * ageFactor,
    minerals: 1000 * weightFactor,
    aminoAcids: Math.max(p.weightKg, 1) * 800,
    fattyAcids: 65000 * weightFactor,
    micros: Object.fromEntries(plan.micros.map(m => [m.key, { value: m.unit === 'µg' ? m.value / 1000 : m.value, text: `${m.value} ${m.unit}`, max: m.max }])),
  };
}

export const MICRONUTRIENT_WARNING_RATIO = 0.8;
